import type {
  DexDocumentType,
  DexPipelineStage,
  DexProcessingStatus,
  DexSourceType,
} from "#graphql/supergraphTypes";

// Placeholder for columns with no value yet (e.g. type/fields before extraction runs).
export const EMPTY = "—";

const SOURCE_LABELS: Record<DexSourceType, string> = {
  MANUAL_UPLOAD: "Manual upload",
};

const STAGE_LABELS: Record<DexPipelineStage, string> = {
  EXTRACT: "Extract",
  STORE: "Store",
  CATALOG: "Catalog",
};

const DOCUMENT_TYPE_LABELS: Record<DexDocumentType, string> = {
  K1: "K-1",
  PCAP: "PCAP",
};

// Keyed by wire enum value with string keys (not DexProcessingStatus): dex's
// classify/extract lifecycle statuses aren't in the generated supergraph type
// yet, and any value still missing here falls back to a humanized label so the
// status tag is never blank.
const STATUS_LABELS: Record<string, string> = {
  RUNNING: "Running",
  DONE: "Done",
  FAILED: "Failed",
  CLASSIFY: "Classifying",
  EXTRACT: "Extracting",
  CLASSIFICATION_FAILED: "Classification failed",
  CLASSIFICATION_UNRELIABLE: "Classification unreliable",
  CLASSIFICATION_TIMEOUT: "Classification timeout",
};

type StatusTagVariant = "informative" | "success" | "danger";

const STATUS_TAG_VARIANTS: Record<string, StatusTagVariant> = {
  RUNNING: "informative",
  DONE: "success",
  FAILED: "danger",
  CLASSIFY: "informative",
  EXTRACT: "informative",
  CLASSIFICATION_FAILED: "danger",
  CLASSIFICATION_UNRELIABLE: "danger",
  CLASSIFICATION_TIMEOUT: "danger",
};

// "CLASSIFICATION_FAILED" -> "Classification failed". Last-resort label for any
// status not enumerated above.
const humanizeStatus = (status: string): string => {
  const s = status.toLowerCase().replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const formatSource = (sourceId: DexSourceType): string => SOURCE_LABELS[sourceId];

export const formatStage = (stage: DexPipelineStage): string => STAGE_LABELS[stage];

export const formatDocumentType = (documentType: DexDocumentType | null): string =>
  documentType === null ? EMPTY : DOCUMENT_TYPE_LABELS[documentType];

export const formatFieldsExtracted = (fieldsExtracted: number | null): string =>
  fieldsExtracted === null ? EMPTY : String(fieldsExtracted);

export const formatReceivedAt = (receivedAt: string): string =>
  // Narrow/no-break spaces from Intl vary by ICU build; normalize to a plain space.
  new Date(receivedAt)
    .toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
    .replace(/[\u202f\u00a0]/g, " ");

export const statusLabel = (status: DexProcessingStatus): string =>
  STATUS_LABELS[status] ?? humanizeStatus(status);

export const statusTagVariant = (status: DexProcessingStatus): StatusTagVariant =>
  STATUS_TAG_VARIANTS[status] ?? "informative";
